import React from "react";
import {
    YearGroup,
    YearAccordionHeader,
    YearAccordionTitle,
    YearAccordionRight,
    CompletedText
} from "../styles/primitives";
import ModuleRow from "./ModuleRow";
import ModuleExpanded from "./ModuleExpanded";
import { 
    yearLabels, 
    normalizeModuleId, 
    isPassedStatus, 
    getModuleDescription,
    countsTowardsModuleLimit,
    deriveModuleSource,
    isModuleSelectedByIdOrName
} from "../../shared/utils/moduleHelpers";

const YearAccordion = ({ 
    year, 
    modules, 
    isExpanded, 
    onToggle, 
    selectedModules, 
    isEditMode, 
    expandedModules,
    onToggleModuleExpand,
    onRemoveModule,
    onAddModule,
    isSelectionDisabled,
    allModules,
    readOnly
}) => {
    const yearLabel = yearLabels[year] || year;
    
    const { selectedDelta, enrolledCount, selectedAnyCount } = modules.reduce(
        (acc, module) => {
            const isSelected = isModuleSelectedByIdOrName(
                module.netsuite_product_id,
                module.name,
                selectedModules,
                allModules
            );

            const statusText = String(module?.status || "").trim().toLowerCase();
            const reasonText = String(module?.reason || "").trim().toLowerCase();
            const isEnrolled = statusText === "enrolled" || reasonText === "enrolled";
            if (isEnrolled) acc.enrolledCount += 1;

            if (isSelected) {
                acc.selectedAnyCount += 1;
                const moduleSource = module.moduleSource || deriveModuleSource(module);
                const selected = moduleSource !== "transfer" ;
                acc.selectedDelta += selected ? 1 : 0;
            }

            return acc;
        },
        { selectedDelta: 0, enrolledCount: 0, selectedAnyCount: 0 }
    );

    const selectedCountClamped = Math.max(0, selectedDelta);

    return (
        <YearGroup>
            <YearAccordionHeader $collapsed={!isExpanded} onClick={onToggle}>
                <YearAccordionTitle>{yearLabel} Year Modules</YearAccordionTitle>
                <YearAccordionRight>
                    {!readOnly && (selectedAnyCount > 0 || enrolledCount > 0) && (
                        <CompletedText>
                           
                            {enrolledCount > 0 && ( 
                                <>
                                <i className="fal fa-book" style={{ color: "var(--white)" }}></i>
                                <span style={{ fontWeight: 600 }}>
                                    {enrolledCount} enrolled
                                </span>
                                </>
                            )}
                             {selectedAnyCount > 0 && (
                                <>
                                    <i className="fal fa-check-circle" style={{ color: "var(--white)" }}></i>
                                    <span style={{ fontWeight: 600 }}>{selectedCountClamped} Selected</span>
                                </>
                            )}
                        </CompletedText>
                    )}
                    <i className={`fa-light ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ fontSize: "20px" }}></i>
                </YearAccordionRight>
            </YearAccordionHeader>
            {isExpanded && modules.map((module, idx) => {
                const isModuleExpanded = expandedModules?.some(id => normalizeModuleId(id) === normalizeModuleId(module.netsuite_product_id));
                const isSelected = isModuleSelectedByIdOrName(
                    module.netsuite_product_id,
                    module.name,
                    selectedModules,
                    allModules
                );
                const moduleSource = deriveModuleSource(module);
                
                const hasAnyPrerequisites = module.pre_requisites && 
                    module.pre_requisites !== "[0]" && 
                    module.pre_requisites !== "[]" && 
                    typeof module.pre_requisites === "object" &&
                    Object.keys(module.pre_requisites).length > 0;
                
                const moduleDescriptionText = getModuleDescription(module);
                const hasDescription = Boolean(moduleDescriptionText);
                const rawPrerequisites = hasAnyPrerequisites ? Object.entries(module.pre_requisites) : [];
                const visiblePrerequisites = rawPrerequisites.filter(([, prereq]) => !isPassedStatus(prereq.reason || prereq.status));
                const hasVisiblePrerequisites = visiblePrerequisites.length > 0;
                const canExpand = hasDescription || hasVisiblePrerequisites;
                const shouldDisableAddButton = isSelectionDisabled && countsTowardsModuleLimit(moduleSource) && !isSelected;

                return (
                    <React.Fragment key={module.netsuite_product_id || idx}>
                        <ModuleRow
                            module={module}
                            isFirst={idx === 0}
                            isLast={idx === modules.length - 1}
                            isSelected={isSelected}
                            isEditMode={isEditMode}
                            isExpanded={isModuleExpanded}
                            canExpand={canExpand}
                            shouldDisableAddButton={shouldDisableAddButton}
                            onAddModule={onAddModule}
                            onRemoveModule={onRemoveModule}
                            onToggleExpand={() => onToggleModuleExpand(module.netsuite_product_id)}
                        />
                        {isEditMode && isModuleExpanded && (
                            <ModuleExpanded
                                module={module}
                                visiblePrerequisites={visiblePrerequisites}
                                isEditMode={isEditMode}
                                selectedModules={selectedModules}
                                isSelectionDisabled={isSelectionDisabled}
                                onRemoveModule={onRemoveModule}
                                onAddModule={onAddModule}
                                allModules={allModules}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </YearGroup>
    );
};

export default YearAccordion;
